import React, { useState, useCallback, useEffect } from "react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useInView } from "react-intersection-observer";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Caption1,
  CompleteCheckbox,
  SkeletonLoader,
  Tooltip,
  WarningTriangleSmallIcon,
} from "@activecollab/components";
import classNames from "classnames";
import { debounce } from "lodash";
import PropTypes from "prop-types";
import { useAssigneeTitle } from "~/components/AssigneePicker/useAssigneeTitle";
import { useMyTasksColumnsContext } from "~/components/MyWork/MyTasks/context";
import {
  getConfigOption,
  getPersonalizedDateFormat,
  getRawConfigOption,
} from "~/store/selectors/selector.configOptions.jsx";
import { getTaskById, makeIsTaskOverdueSelector } from "~/store/selectors/selector.tasks.jsx";
import { formatDateRange } from "~/utils/date.jsx";
import SubtaskAsListItem from "./SubtaskAsListItem.jsx";
import { SubtaskDatePicker } from "./SubtaskDatePicker.jsx";
import SubtaskForm from "./SubtaskForm.jsx";
import SubtaskPanel from "./SubtaskPanel.jsx";
import ElementPlaceholder from "../ElementPlaceholder/ElementPlaceholder.js";
import { ProjectMembersAssigneePicker } from "../ProjectMembersAssigneePicker.jsx";
import AvatarContainer from "../Users/AvatarContainer.jsx";

const SubtaskItem = ({
  id,
  pending,
  canModify,
  editMode,
  onCancelEdit,
  taskId,
  projectId,
  onStartEdit,
  convertToTaskReact,
  isAssigneeHidden,
  isTaskNameVisible,
  isProjectNameVisible,
  isLink,
  onComplete,
  assigneeId,
  fakeAssigneeName,
  name,
  onChange,
  dueOn,
  isCompleted,
  loadingDueDateReschedule,
  className,
}) => {
  // @todo: check if this state is still relevant
  const [pendingListChange] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const userName = useAssigneeTitle(assigneeId, fakeAssigneeName);
  const task = useSelector((state) => getTaskById(state, taskId));
  const autoReschedule = useSelector((state) => getRawConfigOption(state, "auto_reschedule_enabled"));
  const showTaskNumber = useSelector((state) => getConfigOption(state, `show_task_id`));
  const { columns } = useMyTasksColumnsContext();
  const { ref, inView } = useInView();
  const [subtaskInView, setSubtaskInView] = useState(false);
  const format = useSelector(getPersonalizedDateFormat);
  const getIsTaskOverdue = useMemo(makeIsTaskOverdueSelector, []);
  const isOverdue = useSelector((state) => getIsTaskOverdue(state, taskId));
  const isInConflict = task?.get("due_on") && dueOn > task?.get("due_on");
  const formattedDate = formatDateRange(dueOn, dueOn, format);

  const handleCompleteTogglerClick = useCallback(
    (e) => {
      if (canModify && typeof onComplete === "function") {
        e.stopPropagation();
        onComplete(id);
      }
    },
    [onComplete, canModify, id]
  );

  const handleDoubleClick = useCallback(() => {
    if (canModify && typeof onStartEdit === "function" && !isAssigneeHidden) {
      onStartEdit(id);
    }
  }, [canModify, onStartEdit, id, isAssigneeHidden]);

  const handleChangeAssignee = useCallback(
    (id) => {
      if (typeof onChange === "function") {
        onChange({ assignee_id: id });
      }
    },
    [onChange]
  );
  const handleDateChange = useCallback(
    ({ to }) => {
      if (typeof onChange === `function`) {
        onChange({
          due_on: to,
        });
      }
    },
    [onChange]
  );
  const handleOpenTaskModal = useCallback(() => {
    if (columns && !editMode) {
      navigate(`?modal=Task-${taskId}-${projectId}`);
    }
  }, [columns, editMode, navigate, projectId, taskId]);

  const debouncedInView = useMemo(
    () =>
      debounce(async () => {
        if (inView) {
          return setSubtaskInView(true);
        }
        setSubtaskInView(false);
      }, 300),
    [inView]
  );

  useEffect(() => {
    debouncedInView();
  }, [debouncedInView]);

  return (
    <div
      className={classNames("task subtask subtask_item", className, {
        "tw-cursor-pointer": columns && !editMode,
      })}
      onClick={handleOpenTaskModal}
      ref={ref}
    >
      {editMode ? (
        <SubtaskForm
          id={id}
          hiddenFromClient={task.get("is_hidden_from_clients")}
          projectId={projectId}
          handleCancel={onCancelEdit}
          name={name}
          assigneeId={assigneeId}
          onSubmit={onChange}
          submitText={t("Update Subtask")}
          dueOn={dueOn}
          taskDueOn={task?.get("due_on")}
          isCompleted={isCompleted}
        />
      ) : (
        <div
          className={classNames("tw-flex flex-row tw-flex-wrap task_view_mode tw-items-start", {
            pending,
            "tw-pr-8": !canModify,
          })}
          onDoubleClick={handleDoubleClick}
        >
          {canModify ? (
            <div className="tw-h-8 tw-flex tw-items-start tw-pt-1">
              <CompleteCheckbox
                onClick={handleCompleteTogglerClick}
                className={classNames("tw-mr-2 tw-shrink-0", {
                  "tw-mr-3": showTaskNumber,
                })}
              />
            </div>
          ) : null}
          <SubtaskAsListItem
            id={id}
            pendingListChange={pendingListChange}
            isTaskNameVisible={isTaskNameVisible}
            isProjectNameVisible={isProjectNameVisible}
            isLink={isLink}
            taskId={taskId}
            projectId={projectId}
            name={name}
            assigneeId={assigneeId}
            dueOn={dueOn}
            isCompleted={isCompleted}
            taskDueOn={task?.get("due_on")}
          />
          {isProjectNameVisible || isTaskNameVisible ? (
            <ElementPlaceholder className="tw-h-6 tw-w-6 tw-shrink-0 tw-hidden lg:tw-flex lg:tw-invisible" />
          ) : null}
          {columns && !isAssigneeHidden ? (
            <ProjectMembersAssigneePicker
              projectId={projectId}
              assigneeId={assigneeId}
              fakeAssigneeName={fakeAssigneeName}
              onChange={handleChangeAssignee}
              hiddenFromClient={task.is_hidden_from_clients}
              className={classNames({
                "show-on-hover": !assigneeId,
              })}
              position="bottom-end"
            />
          ) : null}
          {columns ? (
            columns.map(({ value, name, active }, index) => {
              if (name === "Date" && value && active) {
                return subtaskInView ? (
                  <div
                    key={`name-${name}-${index}`}
                    className="col col-date tw-justify-end tw-hidden lg:tw-flex subtask_options has_due_on"
                  >
                    <>
                      {loadingDueDateReschedule && dueOn && autoReschedule ? (
                        <div className="tw-flex tw-flex-col tw-justify-end tw-items-center tw-w-16">
                          <SkeletonLoader />
                        </div>
                      ) : (
                        <SubtaskDatePicker
                          dueOn={dueOn}
                          taskDueOn={task?.get("due_on")}
                          isCompleted={isCompleted}
                          handleDateChange={handleDateChange}
                          changeMode="atomic"
                          userId={typeof assigneeId === "number" ? assigneeId : undefined}
                          canModify={canModify}
                        />
                      )}
                    </>
                  </div>
                ) : dueOn ? (
                  <div
                    key={`name-${name}-${index}`}
                    className="col col-date tw-justify-end tw-hidden lg:tw-flex"
                  >
                    <Caption1
                      key={`name-${name}-${index}`}
                      lineHeight="loose"
                      className={classNames("tw-px-2 tw-h-6 tw-pt-[6px]", {
                        "tw-text-red-alert": isOverdue || isInConflict,
                      })}
                      weight="bold"
                    >
                      {isInConflict ? <WarningTriangleSmallIcon className="tw-mr-2 tw-fill-red-alert" /> : null}
                      {formattedDate}
                    </Caption1>
                  </div>
                ) : (
                  <div
                    key={`name-${name}-${index}`}
                    className="col col-date tw-hidden lg:tw-flex"
                  ></div>
                );
              }
              if (name === "Label" && value && active) {
                return (
                  <div
                    key={`name-${name}-${index}`}
                    className="col col-label tw-hidden lg:tw-flex"
                  ></div>
                );
              }
              if (name === "Time" && value && active) {
                return (
                  <div
                    key={`name-${name}-${index}`}
                    className="col col-time tw-hidden lg:tw-flex"
                  ></div>
                );
              }
            })
          ) : (
            <div
              className={classNames(
                "tw-h-8 subtask_assignee_wrapper tw-flex tw-items-center tw-justify-center subtask_options",
                {
                  has_assignee: !isAssigneeHidden,
                  has_due_on: dueOn > 0,
                }
              )}
            >
              <>
                {loadingDueDateReschedule && dueOn && autoReschedule ? (
                  <div className="tw-flex tw-flex-col tw-justify-end tw-items-center tw-w-16">
                    <SkeletonLoader />
                  </div>
                ) : (
                  <SubtaskDatePicker
                    dueOn={dueOn}
                    taskDueOn={task?.get("due_on")}
                    isCompleted={isCompleted}
                    handleDateChange={handleDateChange}
                    changeMode="atomic"
                    userId={typeof assigneeId === "number" ? assigneeId : undefined}
                    canModify={canModify}
                  />
                )}
              </>

              {!isAssigneeHidden ? (
                canModify ? (
                  <ProjectMembersAssigneePicker
                    assigneeId={assigneeId}
                    projectId={projectId}
                    fakeAssigneeName={fakeAssigneeName}
                    onChange={handleChangeAssignee}
                    hiddenFromClient={task.get("is_hidden_from_clients")}
                  />
                ) : assigneeId ? (
                  <Tooltip title={userName}>
                    <AvatarContainer
                      id={assigneeId}
                      className="tw-ml-1"
                    />
                  </Tooltip>
                ) : dueOn ? (
                  <div className="tw-w-6 tw-ml-1"></div>
                ) : null
              ) : null}
            </div>
          )}
          {canModify ? (
            <SubtaskPanel
              id={id}
              projectId={projectId}
              taskId={taskId}
              onEditSubtask={onStartEdit}
              convertToTaskReact={convertToTaskReact}
            />
          ) : null}
        </div>
      )}
    </div>
  );
};

SubtaskItem.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  pending: PropTypes.bool,
  editMode: PropTypes.bool,
  canModify: PropTypes.bool,
  onComplete: PropTypes.func,
  onStartEdit: PropTypes.func,
  onCancelEdit: PropTypes.func,
  convertToTaskReact: PropTypes.func,
  projectId: PropTypes.number,
  taskId: PropTypes.number,
  isAssigneeHidden: PropTypes.bool,
  isTaskNameVisible: PropTypes.bool,
  isProjectNameVisible: PropTypes.bool,
  isLink: PropTypes.bool,
  assigneeId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  name: PropTypes.string,
  fakeAssigneeName: PropTypes.string,
  onChange: PropTypes.func,
  dueOn: PropTypes.number,
  isCompleted: PropTypes.bool,
  loadingDueDateReschedule: PropTypes.bool,
  className: PropTypes.string,
};

SubtaskItem.defaultProps = {
  editMode: false,
  pending: false,
  canModify: false,
  onComplete: () => null,
  onStartEdit: () => null,
  onCancelEdit: () => null,
  convertToTaskReact: () => null,
  isAssigneeHidden: false,
  isTaskNameVisible: false,
  isLink: false,
  isProjectNameVisible: false,
  assigneeId: 0,
};

SubtaskItem.displayName = "SubtaskItem";

export default SubtaskItem;
